import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { Nullable } from '@kts-front/types';
import { action, computed, makeObservable, observable, reaction, runInAction, when } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import {
  NotificationEntity,
  NotificationsListEntityApi,
  notificationsListSchema,
  notificationsTypeTextsMap,
  notificationType,
} from 'entities/Notification';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { CollectionStore } from 'stores/local/shared/CollectionStore';
import { OpenStore } from 'stores/local/shared/OpenStore';
import { FieldSelectMultiStore } from 'stores/local/shared/form/FieldSelectStore';
import { isCancelledRequest } from 'utils/isCancelledRequest';
import { logZodError } from 'utils/logZodError';

import { NotificationsStore } from '../NotificationsStore';
import { UserStore } from '../UserStore';

type UserNotificationsStoreOptions = {
  userStore: UserStore;
  notificationStore: NotificationsStore;
};

export class UserNotificationsStore {
  private _userStore: UserStore;
  private _notificationStore: NotificationsStore;

  readonly modal = new OpenStore();

  readonly typeField = new FieldSelectMultiStore({
    searchDebounceTimeout: 0,
    getOptions: async () => {
      const items = notificationType.options.map((item) => ({
        key: item.toString(),
        value: notificationsTypeTextsMap[item],
      }));

      return {
        isError: false,
        data: { items, total: items.length },
      };
    },
  });

  readonly loadingStage = new LoadingStageModel();

  private readonly _notificationsRequest = apiStore.createRequest({ url: ENDPOINTS.notice.list });

  readonly list = new CollectionStore<NotificationEntity, NotificationEntity, number | string>({
    getEntityKey: ({ id }) => id,
    parseItem: (e) => e,
  });

  total: number = 0;
  readonly unReadCount = new ValueModel<Nullable<number>>(null);

  constructor({ userStore, notificationStore }: UserNotificationsStoreOptions) {
    this._userStore = userStore;
    this._notificationStore = notificationStore;

    makeObservable(this, {
      fetchNotifications: action.bound,
      total: observable,
      canLoadMore: computed,
      onClose: action.bound,
    });

    this.typeField.load();

    reaction(
      () => this.modal.isOpen,
      (isOpen) => {
        if (isOpen) {
          this.fetchNotifications(true);
        }
      },
    );

    reaction(
      () => this.typeField.value,
      () => {
        this.fetchNotifications(true);
      },
    );

    when(
      () => Boolean(this._userStore.user),
      () => {
        this.unReadCount.change(this._userStore.user?.notificationsUnreadCount ?? null);
      },
    );
  }

  onClose() {
    this.modal.close();
    this.typeField.reset();
  }

  async fetchNotifications(reload: boolean = false) {
    this.loadingStage.loading();

    if (reload) {
      this.list.fromArray([]);
      this.total = 0;
    }

    const requestPayload = {
      types: this.typeField.serverFormat,
      limit: 24,
      offset: reload ? 0 : this.list.size,
    };

    const response = await this._notificationsRequest.call<NotificationsListEntityApi>({
      params: requestPayload,
    });

    runInAction(() => {
      if (response.isError) {
        if (isCancelledRequest(response)) {
          this.loadingStage.reset();

          return;
        }

        logApiErrWithSentry({
          endpoint: ENDPOINTS.notice.list,
          method: 'GET',
          requestPayload,
          response,
        });

        this.loadingStage.error();
        this._notificationStore.showErrorByResponse(response);

        return;
      }

      const parsed = notificationsListSchema.safeParse(response.data);

      if (parsed.error) {
        logZodError(parsed.error);
        this.loadingStage.error();
        this._notificationStore.showError({ message: parsed.error.message });

        return;
      }

      if (reload) {
        this.list.fromArray(parsed.data.items);
      } else {
        this.list.pushRaws(parsed.data.items);
      }

      this.total = parsed.data.total;
      this.unReadCount.change(parsed.data.unreadCount);
      this.loadingStage.success();
    });
  }

  get canLoadMore(): boolean {
    if (this.total === null) {
      return true;
    }

    if (this.loadingStage.isLoading) {
      return false;
    }

    return this.list.size < this.total;
  }
}
